import { useVerificationsOverviewController } from "shared/queries/useVerificationsOverviewController";
import { VerificationNames, VerificationStatus } from "shared/types";
import { has, formatToCurrency } from "shared/utils";
import { Money } from "types/landlord/reports";
import { AuditSummaryFlag } from "views/applications-id/components/ApplicantsTable/components/AuditSummaryFlag";
import { Cell } from "views/applications-id/components/ApplicantsTable/components/Cell";
import { ReportStatusBadge } from "views/applications-id/components/ApplicantsTable/components/ReportStatusBadge";
import {
  FullApplicant,
  Verification
} from "views/applications-id/components/ApplicantsTable/types";

const DataPoint = ({
  val,
  applicant,
  hasAuditFlagEnabled
}: {
  val: string | number | undefined | null | Money;
  applicant: FullApplicant;
  hasAuditFlagEnabled: boolean;
}) => {
  if (has(val) && typeof val === "object" && "cents" in val)
    return <span>{formatToCurrency(val)}</span>;

  if (hasAuditFlagEnabled)
    return (
      <AuditSummaryFlag audit_summary_flags={applicant.audit_summary_flags} />
    );

  return <span>-</span>;
};

export const CombinedIncomeReportSection = ({
  applicant,
  verification,
  hasAuditFlagEnabled
}: {
  applicant: FullApplicant;
  hasAuditFlagEnabled: boolean;
  verification: Verification;
}) => {
  const { verificationsOverviewMeta } = useVerificationsOverviewController({
    applicationId: Number(applicant.application_id),
    applicantId: Number(applicant.id)
  });

  return (
    <>
      {/* Empty cell for all rows */}
      <Cell className="bg-background-900" />
      <Cell isDataCell className="relative">
        {/* Gross income (Verified) */}
        <DataPoint
          applicant={applicant}
          val={verification.summary?.verified_gross_monthly_total}
          hasAuditFlagEnabled={hasAuditFlagEnabled}
        />
        <ReportStatusBadge
          verificationStatus={
            verificationsOverviewMeta.data?.[VerificationNames.CombinedIncome]
              ?.has_warnings
              ? VerificationStatus.FinishedWithWarnings
              : verification.status
          }
        />
      </Cell>
      {/* Net income (Verified) */}
      <Cell isDataCell>
        <DataPoint
          applicant={applicant}
          val={verification.summary?.verified_net_monthly_total}
          hasAuditFlagEnabled={hasAuditFlagEnabled}
        />
      </Cell>
      {/* Gross income (Self-reported) */}
      <Cell isDataCell>
        <DataPoint
          applicant={applicant}
          val={verification.summary?.self_reported_gross_monthly_total}
          hasAuditFlagEnabled={hasAuditFlagEnabled}
        />
      </Cell>
    </>
  );
};
